import type { SessionRow, WeekRow } from '../../shared/queries';

/**
 * Which day a session falls on, and therefore whether it is today.
 *
 * Today's whole job is to answer one question, and the database does not quite
 * answer it: a session carries `day_of_week` 1-7 and its week carries an
 * optional `starts_on`. Turning that into "is this today" is the arithmetic in
 * this file, kept out of the screen so it can be tested without rendering
 * anything and so there is exactly one answer to it.
 *
 * Three decisions are worth stating, because none of them is forced:
 *
 * 1. **A day is a `YYYY-MM-DD` string, and days are compared as strings.**
 *    Comparing `Date` objects across a timezone or a daylight-saving change is
 *    how "today" becomes "yesterday" for a client who travels, and this product
 *    has already had that bug once (`src/shared/formatDate.ts`). Lexicographic
 *    order on an ISO day is calendar order, exactly, with nothing to get wrong.
 *
 * 2. **`starts_on` is read as the week's Monday**, and a `starts_on` that is not
 *    a Monday is snapped back to the Monday of its own week. `day_of_week` is
 *    ISO — 1 is Monday, which is what `weekdayNames()` renders — so the two have
 *    to agree about where a week begins or a session lands on the wrong day.
 *
 * 3. **A week with no `starts_on` is this week.** Most weeks in this product
 *    have none: the coach's editor leaves the date empty unless she fills it in,
 *    so every week built so far is undated. Refusing to place them would mean
 *    Today answering "nothing scheduled" for every client with a real plan,
 *    which is worse than the assumption. A dated week always wins over an
 *    undated one, so filling the date in is what corrects it.
 */

/** A calendar day, `YYYY-MM-DD`. Compare these as strings; that is the point. */
export type ISODay = string;

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** A `Date` as the calendar day it is in the reader's own zone. */
export function toISODay(date: Date): ISODay {
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Today, as a calendar day. Takes a clock so a test can hold one still. */
export function today(now: Date = new Date()): ISODay {
  return toISODay(now);
}

/** A calendar day as a local `Date` at midnight — never parsed as UTC. */
function parseDay(day: ISODay): Date {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, date ?? 1);
}

/** `n` days after a calendar day. Local arithmetic, so it survives a clock change. */
export function addDays(day: ISODay, count: number): ISODay {
  const moved = parseDay(day);
  moved.setDate(moved.getDate() + count);
  return toISODay(moved);
}

/** ISO weekday of a calendar day — Monday 1 … Sunday 7. */
export function weekdayOf(day: ISODay): number {
  const index = parseDay(day).getDay();
  return index === 0 ? 7 : index;
}

/** The Monday of the week a calendar day falls in. */
export function mondayOf(day: ISODay): ISODay {
  return addDays(day, 1 - weekdayOf(day));
}

/** The Monday a week's days are counted from. See decisions 2 and 3 above. */
export function weekStart(week: WeekRow, now: ISODay): ISODay {
  return mondayOf(week.starts_on ?? now);
}

/** The calendar day a session falls on. */
export function sessionDay(week: WeekRow, session: SessionRow, now: ISODay): ISODay {
  return addDays(weekStart(week, now), session.day_of_week - 1);
}

function contains(week: WeekRow, now: ISODay): boolean {
  const start = weekStart(week, now);
  return now >= start && now <= addDays(start, 6);
}

/**
 * The week today is in.
 *
 * A week that actually covers today wins. Where none does — every week is dated
 * and they are all in the past, say — the latest one by number is used, so the
 * client still sees the most recent thing their coach published rather than
 * nothing at all. An undated week always covers today, by decision 3.
 */
export function currentWeek(weeks: readonly WeekRow[], now: ISODay): WeekRow | null {
  const latest = (rows: readonly WeekRow[]): WeekRow | null =>
    rows.reduce<WeekRow | null>(
      (best, week) => (best === null || week.week_number > best.week_number ? week : best),
      null,
    );

  return latest(weeks.filter((week) => contains(week, now))) ?? latest(weeks);
}

/** Every session of a week, paired with the day it falls on. */
export function datedSessions(
  week: WeekRow,
  sessions: readonly SessionRow[],
  now: ISODay,
): { session: SessionRow; day: ISODay }[] {
  return sessions.map((session) => ({ session, day: sessionDay(week, session, now) }));
}

/** The session planned for today, or `null`. The first, if a day has two. */
export function sessionToday(
  week: WeekRow,
  sessions: readonly SessionRow[],
  now: ISODay,
): SessionRow | null {
  return datedSessions(week, sessions, now).find((row) => row.day === now)?.session ?? null;
}

/**
 * The soonest session after today, across the weeks given, or `null`.
 *
 * Called with this week and the one after it, which is as far ahead as Today
 * needs to look: "where a next one is known" is the requirement, not "always".
 */
export function nextSession(
  weeks: readonly { week: WeekRow; sessions: readonly SessionRow[] }[],
  now: ISODay,
): { session: SessionRow; day: ISODay } | null {
  const upcoming = weeks
    .flatMap(({ week, sessions }) => datedSessions(week, sessions, now))
    .filter((row) => row.day > now)
    .sort((a, b) => a.day.localeCompare(b.day));

  return upcoming[0] ?? null;
}

/** The week after the given one, by number, among the weeks a client can see. */
export function weekAfter(weeks: readonly WeekRow[], week: WeekRow): WeekRow | null {
  return weeks
    .filter((row) => row.week_number > week.week_number)
    .reduce<WeekRow | null>(
      (best, row) => (best === null || row.week_number < best.week_number ? row : best),
      null,
    );
}

/**
 * The seven calendar days of a week, Monday first.
 *
 * Index n is `day_of_week` n+1, the same indexing `weekdayNames()` uses, so a
 * session lands in the cell its number names without any screen doing the
 * arithmetic twice.
 *
 * **This is the function that assumes every session has a day.** The parked
 * `session-scheduling-step` makes `session.day_of_week` nullable and adds "any
 * day this week"; a session with no day has no cell here and would simply
 * vanish from the week. Whoever unparks that change starts here. See the note at
 * the end of this change's `tasks.md`.
 */
export function weekDays(week: WeekRow, now: ISODay): ISODay[] {
  const start = weekStart(week, now);
  return [0, 1, 2, 3, 4, 5, 6].map((offset) => addDays(start, offset));
}

/** The week with this number among the weeks a client can see, or `null`. */
export function weekByNumber(weeks: readonly WeekRow[], number: number): WeekRow | null {
  return weeks.find((week) => week.week_number === number) ?? null;
}

/** The week before the given one, by number, among the weeks a client can see. */
export function weekBefore(weeks: readonly WeekRow[], week: WeekRow): WeekRow | null {
  return weeks
    .filter((row) => row.week_number < week.week_number)
    .reduce<WeekRow | null>(
      (best, row) => (best === null || row.week_number > best.week_number ? row : best),
      null,
    );
}

/** The highest-numbered week a client can see, or `null` when there are none. */
export function lastWeek(weeks: readonly WeekRow[]): WeekRow | null {
  return weeks.reduce<WeekRow | null>(
    (best, week) => (best === null || week.week_number > best.week_number ? week : best),
    null,
  );
}

/**
 * What a session is, on the week view.
 *
 * Four states and no fifth, and the names are chosen as carefully as the words
 * on the screen are:
 *
 *   * `done` — the client completed it, whenever that was. **There is no
 *     "done late".** `mvp-discovery` describes one, and the product does not
 *     draw it: a session completed on Thursday that was planned for Tuesday is
 *     done, and a label that says *when* it was done relative to a plan is a
 *     verdict wearing a fact's clothes (`client-tone`, "A session not done is
 *     stated neutrally"). The client can see the date it was planned for and the
 *     time they finished it, which is the fact without the judgement.
 *   * `today` — it is today's.
 *   * `ahead` — its day has not arrived.
 *   * `open` — its day has passed and it was not completed. Not "missed", not
 *     "skipped": it is open, and it stays open.
 *
 * Completion is a fact about the device and nothing else; see `./progress.ts`.
 */
export type SessionState = 'done' | 'today' | 'ahead' | 'open';

export function sessionState(day: ISODay, now: ISODay, completedAt: string | null): SessionState {
  if (completedAt !== null) return 'done';
  if (day === now) return 'today';
  return day > now ? 'ahead' : 'open';
}
